import { ArrowUpRight, Play, Clapperboard } from "lucide-react";
import SectionHeading, { Reveal } from "./SectionHeading";
import GamePhotoStrip from "./GamePhotoStrip";

const pad = (n) => String(n).padStart(2, "0");

export default function Highlights({ clips, gcClips = [], gcProfile }) {
  const hasRealClips = gcClips.length > 0;

  return (
    <section
      id="highlights"
      data-testid="section-highlights"
      className="relative mx-auto max-w-7xl overflow-hidden px-6 py-24 md:py-36"
    >
      <GamePhotoStrip />
      <div className="relative z-10">
      <SectionHeading
        number="02"
        title="THE HIGHLIGHT REEL"
        sub={hasRealClips ? "Live from GameChanger" : "Clips live on GameChanger"}
        id="highlights"
      />

      <Reveal>
        <a
          data-testid="gc-clips-banner"
          href={gcProfile}
          target="_blank"
          rel="noreferrer"
          className="group mb-6 flex flex-col gap-5 rounded-2xl border border-neon/40 bg-panel/70 p-7 transition-colors duration-300 hover:border-neon sm:flex-row sm:items-center sm:justify-between"
        >
          <div className="flex items-center gap-5">
            <span className="flex h-14 w-14 shrink-0 items-center justify-center rounded-full border border-neon/50 text-neon transition-shadow duration-300 group-hover:shadow-[0_0_25px_rgba(57,255,20,0.4)]">
              <Clapperboard className="h-6 w-6" />
            </span>
            <div>
              <p className="font-bebas text-3xl leading-none tracking-wide">
                WATCH THE CLIPS ON GAMECHANGER
              </p>
              <p className="mt-2 max-w-xl text-sm text-white/50">
                {hasRealClips
                  ? "Fresh clips auto-pull onto this page — the full reel lives on Big Cal's athlete profile."
                  : "Video clips are added in the GameChanger app after every game — they'll auto-appear right here."}
              </p>
            </div>
          </div>
          <span className="flex shrink-0 items-center gap-2 font-jbmono text-[11px] tracking-[0.25em] text-neon">
            OPEN PROFILE
            <ArrowUpRight className="h-4 w-4 transition-transform duration-300 group-hover:-translate-y-0.5 group-hover:translate-x-0.5" />
          </span>
        </a>
      </Reveal>

      <div className="grid gap-6 md:grid-cols-2">
        {hasRealClips
          ? gcClips.map((c, i) => (
              <Reveal key={i} delay={(i % 2) * 0.1}>
                <div
                  data-testid={`highlight-clip-${i + 1}`}
                  className="group overflow-hidden rounded-2xl border border-white/10 bg-panel/70 transition-colors duration-300 hover:border-neonpurple"
                >
                  <div className="relative aspect-video overflow-hidden bg-black/45">
                    <video
                      data-testid={`highlight-video-${i + 1}`}
                      className="h-full w-full"
                      src={c.src}
                      poster={c.poster || undefined}
                      controls
                      preload="metadata"
                      playsInline
                    />
                  </div>
                  <div className="flex items-center justify-between gap-4 px-5 py-4">
                    <div>
                      <p className="font-jbmono text-[10px] tracking-[0.3em] text-neonpurple">
                        CLIP {pad(i + 1)}
                      </p>
                      <p className="mt-1 font-bebas text-2xl tracking-wide">
                        {c.title}
                      </p>
                    </div>
                    <span className="shrink-0 rounded-full border border-neon/40 px-3 py-1 font-jbmono text-[9px] tracking-[0.2em] text-neon">
                      GAMECHANGER
                    </span>
                  </div>
                </div>
              </Reveal>
            ))
          : clips.map((c, i) => (
              <Reveal key={i} delay={(i % 2) * 0.1}>
                <a
                  data-testid={`highlight-clip-${i + 1}`}
                  href={gcProfile}
                  target="_blank"
                  rel="noreferrer"
                  className="group block overflow-hidden rounded-2xl border border-white/10 bg-panel/70 transition-colors duration-300 hover:border-neonpurple"
                >
                  <div className="relative flex aspect-video items-center justify-center overflow-hidden bg-black/45">
                    <span className="font-bebas text-[7rem] leading-none text-outline-purple transition-transform duration-500 group-hover:scale-110">
                      {pad(i + 1)}
                    </span>
                    <span className="absolute flex h-16 w-16 items-center justify-center rounded-full border border-white/20 bg-black/60 text-white transition-colors duration-300 group-hover:border-neon group-hover:text-neon">
                      <Play className="ml-1 h-6 w-6" />
                    </span>
                  </div>
                  <div className="flex items-center justify-between gap-4 px-5 py-4">
                    <div>
                      <p className="font-jbmono text-[10px] tracking-[0.3em] text-neonpurple">
                        CLIP {pad(i + 1)}
                      </p>
                      <p className="mt-1 font-bebas text-2xl tracking-wide">
                        {c.title}
                      </p>
                    </div>
                    <span className="shrink-0 rounded-full border border-hotpink/40 px-3 py-1 font-jbmono text-[9px] tracking-[0.2em] text-hotpink">
                      {c.tag}
                    </span>
                  </div>
                </a>
              </Reveal>
            ))}
      </div>
      </div>
    </section>
  );
}
